import { useIntl } from 'react-intl';
import {
  Form,
  Input,
  Select,
  Row,
  Col,
  FormInstance,
  Checkbox,
  Button,
} from 'antd';

import FloatingFormItem from '@/components/_common/FloatingFormItem';

import {
  CompaniesInfiniteSelectField,
  Group,
  InputDate,
  InputText,
} from '@/components/_common';
import { useDefaultEmptyFieldError } from '@/hooks';
import {
  DocumentType,
  EmissionGoal,
  EmissionType,
  InvoiceGeneralFormValues,
  OperationDestination,
  PresenceIndications,
} from '@/core/domain/invoice/invoice/Invoice.types';
import InvoiceAddressFields from '../../InvoiceCreateModal/InvoiceAddressFields';
import SituationInvoiceFormField from '../../SituationInvoiceFormField';
import { useLocation } from 'react-router-dom';
import { FaShare } from 'react-icons/fa';
import AntdFaIcon from '@/components/_common/AntdFaIcon';

interface Props {
  form: FormInstance<InvoiceGeneralFormValues>;
}

function InvoiceGeneralForm({ form }: Props) {
  const intl = useIntl();

  const companyIdField = Form.useWatch(['companyId'], form);

  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  const invoiceType = useLocation().pathname.split('/datas')[0].slice(20, 23);

  return (
    <>
      <Form layout="vertical" form={form} name="invoiceGeneral">
        <Form.Item hidden name="customerId" />
        <Form.Item hidden name="supplierId" />
        <Form.Item hidden name="insurerId" />

        <Row gutter={[16, 9]}>
          <Col span={24} lg={24} md={24}>
            <SituationInvoiceFormField
              initialValue={form.getFieldValue('situation')}
            />
          </Col>

          <Col xs={24} sm={24} md={7}>
            <Group>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'invoice.domain.company',
                })}
                name="companyId"
                rules={[defaultEmptyFieldError]}
              >
                <CompaniesInfiniteSelectField allowClear />
              </FloatingFormItem>
              <Button>
                <AntdFaIcon faIcon={<FaShare />} />
              </Button>
            </Group>
          </Col>

          <Col xs={24} sm={12} md={2}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'invoice.domain.model',
              })}
              name="model"
            >
              <Input />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={12} md={2}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'invoice.domain.series',
              })}
              name="serie"
              rules={[defaultEmptyFieldError]}
            >
              <Input />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={12} md={4}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'invoice.domain.number',
              })}
              name="number"
            >
              <InputText onlyNumber />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={12} md={9}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'invoice.domain.invoiceKey',
              })}
              name="key"
            >
              <Input disabled />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={12} md={11}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'invoice.domain.destination',
              })}
              name="natureOfOperation"
              rules={[defaultEmptyFieldError]}
            >
              <Input />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'invoice.domain.emissionGoals',
              })}
              name="emissionGoal"
              rules={[defaultEmptyFieldError]}
            >
              <Select
                allowClear
                style={{ width: '100' }}
                options={[
                  {
                    value: EmissionGoal.nfeNormal,
                    label: intl.formatMessage({
                      id: 'invoice.domain.emissionGoals.normalNfe',
                    }),
                  },
                  {
                    value: EmissionGoal.nfeAdditional,
                    label: intl.formatMessage({
                      id: 'invoice.domain.emissionGoals.additionalNfe',
                    }),
                  },
                  {
                    value: EmissionGoal.nfeAdjustment,
                    label: intl.formatMessage({
                      id: 'invoice.domain.emissionGoals.adjustmentNfe',
                    }),
                  },
                  {
                    value: EmissionGoal.nfeMerchandiseReturn,
                    label: intl.formatMessage({
                      id: 'invoice.domain.emissionGoals.merchandiseReturnNfe',
                    }),
                  },
                ]}
              ></Select>
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={12} md={5}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'invoice.domain.emissionDate',
              })}
              name="emissionDate"
              rules={[defaultEmptyFieldError]}
            >
              <InputDate />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'invoice.domain.documentType',
              })}
              name="documentType"
              rules={[defaultEmptyFieldError]}
            >
              <Select
                allowClear
                style={{ width: '100' }}
                options={[
                  {
                    value: DocumentType.entry,
                    label: intl.formatMessage({
                      id: 'invoice.domain.documentType.entry',
                    }),
                  },
                  {
                    value: DocumentType.exit,
                    label: intl.formatMessage({
                      id: 'invoice.domain.documentType.exit',
                    }),
                  },
                ]}
              ></Select>
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'invoice.domain.finalCostumer',
              })}
              name="finalConsumer"
              rules={[defaultEmptyFieldError]}
            >
              <Select
                allowClear
                style={{ width: '100' }}
                options={[
                  {
                    value: 'true',
                    label: intl.formatMessage({
                      id: 'invoice.domain.true',
                    }),
                  },
                  {
                    value: 'false',
                    label: intl.formatMessage({
                      id: 'invoice.domain.false',
                    }),
                  },
                ]}
              ></Select>
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={12} md={invoiceType === 'NFE' ? 7 : 12}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'invoice.domain.operationDestination',
              })}
              name="operationDestination"
              rules={[defaultEmptyFieldError]}
            >
              <Select
                allowClear
                style={{ width: '100' }}
                options={[
                  {
                    value: OperationDestination.internalOperation,
                    label: intl.formatMessage({
                      id: 'invoice.domain.operationDestination.internalOperation',
                    }),
                  },
                  {
                    value: OperationDestination.interstateOperation,
                    label: intl.formatMessage({
                      id: 'invoice.domain.operationDestination.interstateOperation',
                    }),
                  },
                  {
                    value: OperationDestination.foreignOperation,
                    label: intl.formatMessage({
                      id: 'invoice.domain.operationDestination.foreignOperation',
                    }),
                  },
                ]}
              ></Select>
            </FloatingFormItem>
          </Col>

          {invoiceType === 'NFE' ? (
            <Col xs={24} sm={12} md={5}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'invoice.domain.entryExitDate',
                })}
                name="departureDate"
              >
                <InputDate />
              </FloatingFormItem>
            </Col>
          ) : null}

          <Col xs={24} sm={12} md={13}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'invoice.domain.emissionType',
              })}
              name="emissionType"
              rules={[defaultEmptyFieldError]}
            >
              <Select
                allowClear
                style={{ width: '100' }}
                options={[
                  {
                    value: EmissionType.normalEmission,
                    label: intl.formatMessage({
                      id: 'invoice.domain.emissionType.normal',
                    }),
                  },
                  {
                    value: EmissionType.contingencyFsIa,
                    label: intl.formatMessage({
                      id: 'invoice.domain.emissionType.contingencyFs',
                    }),
                  },
                  {
                    value: EmissionType.contingencyDpec,
                    label: intl.formatMessage({
                      id: 'invoice.domain.emissionType.contingencyDpec',
                    }),
                  },
                  {
                    value: EmissionType.contingencyFsDa,
                    label: intl.formatMessage({
                      id: 'invoice.domain.emissionType.contingencyFsda',
                    }),
                  },
                  {
                    value: EmissionType.contingencySvcAn,
                    label: intl.formatMessage({
                      id: 'invoice.domain.emissionType.contingencySvcan',
                    }),
                  },
                  {
                    value: EmissionType.contingencySvcRs,
                    label: intl.formatMessage({
                      id: 'invoice.domain.emissionType.contingencySvcrs',
                    }),
                  },
                ]}
              ></Select>
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={12} md={11}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'invoice.domain.presenceIndicator',
              })}
              name="presenceIndication"
              rules={[defaultEmptyFieldError]}
            >
              <Select
                allowClear
                style={{ width: '100' }}
                options={[
                  {
                    value: PresenceIndications.notApplicable,
                    label: intl.formatMessage({
                      id: 'invoice.domain.presenceIndicator.notApplicable',
                    }),
                  },
                  {
                    value: PresenceIndications.faceToFaceOperation,
                    label: intl.formatMessage({
                      id: 'invoice.domain.presenceIndicator.faceToFaceOperation',
                    }),
                  },
                  {
                    value:
                      PresenceIndications.nonFaceToFaceOperationOverTheInternet,
                    label: intl.formatMessage({
                      id: 'invoice.domain.presenceIndicator.nonFaceToFaceOperationOverTheInternet',
                    }),
                  },
                  {
                    value: PresenceIndications.nonFaceToFaceOperationCallCenter,
                    label: intl.formatMessage({
                      id: 'invoice.domain.presenceIndicator.nonFaceToFaceOperationCallCenter',
                    }),
                  },
                  {
                    value: PresenceIndications.faceToFaceOperationOffSite,
                    label: intl.formatMessage({
                      id: 'invoice.domain.presenceIndicator.faceToFaceOperationOffSite',
                    }),
                  },
                  {
                    value: PresenceIndications.nonFaceToFaceOperationOthers,
                    label: intl.formatMessage({
                      id: 'invoice.domain.presenceIndicator.nonFaceToFaceOperationOthers',
                    }),
                  },
                ]}
              ></Select>
            </FloatingFormItem>
          </Col>

          <Col xs={24} md={16}>
            <InvoiceAddressFields companyId={companyIdField} form={form} />
          </Col>

          <Col xs={24} sm={12} md={8}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'invoice.domain.brokerIndicator',
              })}
              name="intermediaryIndication"
            >
              <Select
                allowClear
                style={{ width: '100' }}
                options={[
                  {
                    value: 'true',
                    label: intl.formatMessage({
                      id: 'invoice.domain.true',
                    }),
                  },
                  {
                    value: 'false',
                    label: intl.formatMessage({
                      id: 'invoice.domain.false',
                    }),
                  },
                ]}
              ></Select>
            </FloatingFormItem>
          </Col>

          <Col span={24} md={9}>
            <Col span={24}>
              <Form.Item name="includeIcms" noStyle valuePropName="checked">
                <Checkbox style={{ marginTop: '12px' }}>
                  {intl.formatMessage({
                    id: 'invoice.domain.freightIcmsSt',
                  })}
                </Checkbox>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="includeIpi" noStyle valuePropName="checked">
                <Checkbox style={{ marginTop: '12px' }}>
                  {intl.formatMessage({
                    id: 'invoice.domain.freightIpi',
                  })}
                </Checkbox>
              </Form.Item>
            </Col>
          </Col>
        </Row>
      </Form>
    </>
  );
}

export default InvoiceGeneralForm;
